// Pill review scheduling (spaced repetition). Curves and the reasoning behind each constant:
// https://claude.ai/artifact/BW5ksGm5FCYoM4RL9HiYnS (current vs proposed, 2026-10-14)

const DAY_MS = 24 * 60 * 60 * 1000

export const PILL_REVIEW_SETTINGS = {
  FIRST_INTERVAL: { hard: 1, good: 2, easy: 4 }, // days, set by the first rating of a pill
  START_EASE: 2.5,
  MIN_EASE: 1.3,
  MAX_EASE: 2.8,
  EASY_EASE_STEP: 0.15, // easy raises ease
  HARD_EASE_STEP: 0.2, // hard lowers ease
  EASY_BONUS: 1.3, // easy: interval × ease × bonus
  HARD_FACTOR: 0.5, // hard shrinks the interval (never under MIN_INTERVAL)
  MIN_INTERVAL: 1, // days
  MAX_INTERVAL: 180, // days: every pill returns at least twice a year
  LATE_CREDIT: 0.5, // share of the delay that counts as extra interval (good/easy), at most the previous interval
  FUZZ: 0.05, // ± random spread so pills rated together drift apart
}

// Stats written by this algorithm carry `v: STATS_VERSION`. Older stats come from the old algorithm, which gave
// intervals of years after a couple of reviews (e.g. 695 days at count 2), so their interval is rebuilt.
const STATS_VERSION = 2

// Interval (days) to trust for these stats. Old stats: what this algorithm would give after `count` reviews
// ending with `lastRating` (first interval × START_EASE per later review), never more than the stored one
export function getTrustedInterval(stats) {
  const S = PILL_REVIEW_SETTINGS
  if (!stats?.interval) return 0
  if (stats.v === STATS_VERSION) return Math.min(stats.interval, S.MAX_INTERVAL)
  const first = S.FIRST_INTERVAL[stats.lastRating] ?? S.FIRST_INTERVAL.good
  const rebuilt = first * Math.pow(S.START_EASE, Math.max(0, (stats.count || 1) - 1))
  return Math.min(stats.interval, rebuilt, S.MAX_INTERVAL)
}

// Due date from the trusted interval, so pills scheduled by the old algorithm come back when plausible
export function getNextReview(stats) {
  if (!stats?.nextReview) return 0
  if (!stats.lastReview || !stats.interval) return stats.nextReview
  return Math.min(stats.nextReview, stats.lastReview + getTrustedInterval(stats) * DAY_MS)
}

// Review status of a pill for lists: label, icon and its colour class (my-status-*, colours in quasar.variables.scss $status-*;
// the pill row strip uses the same class)
export function getPillReviewStatus(stats) {
  if (stats?.learned) return { key: 'learned', icon: 'mdi-check', textClass: 'my-status-learned', text: 'learned' }
  if (stats?.archived) return { key: 'archived', icon: 'mdi-archive', textClass: 'my-status-archived', text: 'archived' }
  if (getNextReview(stats) <= Date.now()) return { key: 'due', icon: 'mdi-clock', textClass: 'my-status-due', text: 'to review' }
  return { key: 'later', icon: 'mdi-clock', textClass: 'my-status-later', text: 'review' }
}

// New review stats after a rating ('easy' | 'good' | 'hard'). Keeps every other field of statsPayload (flags); `count` = reviews
// before this one (the caller adds this one afterwards)
// fuzz: false gives the exact interval (rating button hints)
export function calculateNewPillReviewStats({ statsPayload, rating, fuzz = true }) {
  const S = PILL_REVIEW_SETTINGS
  const prev = statsPayload || {}
  const now = Date.now()
  if (rating === 'again') rating = 'hard'
  let ease = prev.ease ?? S.START_EASE
  let interval

  if (!prev.lastRating || !prev.interval) {
    interval = S.FIRST_INTERVAL[rating] ?? S.FIRST_INTERVAL.good
    if (rating === 'hard') ease -= S.HARD_EASE_STEP
    if (rating === 'easy') ease += S.EASY_EASE_STEP
  } else {
    const prevInterval = getTrustedInterval(prev)
    const daysLate = Math.max(0, (now - (prev.lastReview || now)) / DAY_MS - prevInterval)
    const base = prevInterval + S.LATE_CREDIT * Math.min(daysLate, prevInterval)
    if (rating === 'hard') {
      ease -= S.HARD_EASE_STEP
      interval = prevInterval * S.HARD_FACTOR
    } else if (rating === 'easy') {
      ease += S.EASY_EASE_STEP
      interval = base * Math.min(S.MAX_EASE, Math.max(S.MIN_EASE, ease)) * S.EASY_BONUS
    } else {
      interval = base * Math.min(S.MAX_EASE, Math.max(S.MIN_EASE, ease))
    }
  }

  ease = Math.min(S.MAX_EASE, Math.max(S.MIN_EASE, ease))
  if (fuzz) interval *= 1 + (Math.random() * 2 - 1) * S.FUZZ
  interval = Math.min(S.MAX_INTERVAL, Math.max(S.MIN_INTERVAL, interval))

  return {
    ...prev,
    ease: parseFloat(ease.toFixed(2)),
    interval: parseFloat(interval.toFixed(2)),
    lastReview: now,
    nextReview: Math.round(now + interval * DAY_MS),
    lastRating: rating,
    v: STATS_VERSION,
  }
}
